import {useCallback, useEffect, useMemo, useState} from 'react';
import useYDocStore from '../../../stores/useYDocStore';
import {
    addFolder as yAddFolder,
    addItem as yAddItem,
    ensureSourcePackageSchema,
    getAllFolders,
    getAllItems,
    moveFolder as yMoveFolder,
    moveItem as yMoveItem,
    observeFolders,
    observeItems,
    removeFolder as yRemoveFolder,
    removeItem as yRemoveItem,
    renameFolder as yRenameFolder,
    reorderFolder as yReorderFolder,
    reorderItem as yReorderItem,
} from '../../prosemirror-editor/yMaps/sourcePackageYMap';

/**
 * Hook to read source package data from Y.Map("sourcePackage") reactively.
 * Re-renders whenever any folder or item changes in the shared Y.Doc.
 *
 * Uses separate observers for folders and items so that a change to one
 * does not trigger a re-read of the other. This keeps the number of
 * reconciliations proportional to what actually changed.
 *
 * Replaces `useSourcePackage` — no Zustand report store, no API calls, no pagination.
 *
 * @returns {Object} Source package data and CRUD actions
 */
export function useYSourcePackage() {
    const ydoc = useYDocStore(s => s.ydoc);
    const ySourcePackage = useYDocStore(s => s.ySourcePackage);

    // Initialize to empty arrays — lazy initializers are skipped here because
    // ySourcePackage may be null on first mount (store not yet initialized).
    // The useEffects below will populate state immediately after mount.
    const [folders, setFolders] = useState([]);
    const [items, setItems] = useState([]);

    // Subscribe to folder changes only
    useEffect(() => {
        if (!ySourcePackage) {
            setFolders([]);
            return;
        }

        ensureSourcePackageSchema(ydoc, ySourcePackage);

        // Read initial state
        setFolders(getAllFolders(ySourcePackage));

        return observeFolders(ySourcePackage, () => {
            setFolders(getAllFolders(ySourcePackage));
        });
    }, [ydoc, ySourcePackage]);

    // Subscribe to item changes only
    useEffect(() => {
        if (!ySourcePackage) {
            setItems([]);
            return;
        }

        ensureSourcePackageSchema(ydoc, ySourcePackage);

        // Read initial state
        setItems(getAllItems(ySourcePackage));

        return observeItems(ySourcePackage, () => {
            setItems(getAllItems(ySourcePackage));
        });
    }, [ydoc, ySourcePackage]);

    // ---------------------------------------------------------------------------
    // Stable action callbacks
    // ---------------------------------------------------------------------------

    const addFolder = useCallback(
        (opts) => {
            if (!ydoc || !ySourcePackage) return;
            return yAddFolder(ydoc, ySourcePackage, opts);
        },
        [ydoc, ySourcePackage],
    );

    const renameFolder = useCallback(
        (folderId, name) => {
            if (!ydoc || !ySourcePackage) return;
            return yRenameFolder(ydoc, ySourcePackage, folderId, name);
        },
        [ydoc, ySourcePackage],
    );

    const moveFolder = useCallback(
        (folderId, newParentId, position) => {
            if (!ydoc || !ySourcePackage) return;
            return yMoveFolder(ydoc, ySourcePackage, folderId, newParentId, position);
        },
        [ydoc, ySourcePackage],
    );

    const removeFolder = useCallback(
        (folderId) => {
            if (!ydoc || !ySourcePackage) return;
            return yRemoveFolder(ydoc, ySourcePackage, folderId);
        },
        [ydoc, ySourcePackage],
    );

    const addItem = useCallback(
        (opts) => {
            if (!ydoc || !ySourcePackage) return;
            return yAddItem(ydoc, ySourcePackage, opts);
        },
        [ydoc, ySourcePackage],
    );

    const removeItem = useCallback(
        (itemId) => {
            if (!ydoc || !ySourcePackage) return;
            return yRemoveItem(ydoc, ySourcePackage, itemId);
        },
        [ydoc, ySourcePackage],
    );

    const moveItem = useCallback(
        (itemId, newParentId, position) => {
            if (!ydoc || !ySourcePackage) return;
            return yMoveItem(ydoc, ySourcePackage, itemId, newParentId, position);
        },
        [ydoc, ySourcePackage],
    );

    const reorderFolder = useCallback(
        (folderId, newPosition) => {
            if (!ydoc || !ySourcePackage) return;
            return yReorderFolder(ydoc, ySourcePackage, folderId, newPosition);
        },
        [ydoc, ySourcePackage],
    );

    const reorderItem = useCallback(
        (itemId, newPosition) => {
            if (!ydoc || !ySourcePackage) return;
            return yReorderItem(ydoc, ySourcePackage, itemId, newPosition);
        },
        [ydoc, ySourcePackage],
    );

    const isEmpty = useMemo(
        () => folders.length === 0 && items.length === 0,
        [folders, items],
    );

    return {
        // Data (reactive)
        folders,
        items,
        isEmpty,
        // Write actions
        addFolder,
        renameFolder,
        moveFolder,
        removeFolder,
        addItem,
        removeItem,
        moveItem,
        reorderFolder,
        reorderItem,
    };
}

